import { useQuery } from '@tanstack/react-query';
import { educationDataService, Degree, FieldOfStudy, Country, Institution, EducationLevel } from 'src/services/educationDataService';

// Query keys for react-query
export const educationQueryKeys = {
  all: ['education'] as const,
  degrees: (filters?: { country?: string; level?: string }) => ['education', 'degrees', filters] as const,
  fieldsOfStudy: (filters?: { country?: string; discipline?: string }) => ['education', 'fields', filters] as const,
  countries: () => ['education', 'countries'] as const,
  institutions: (country?: string) => ['education', 'institutions', country] as const,
  educationLevels: (country?: string) => ['education', 'levels', country] as const,
  searchDegrees: (query: string, filters?: { country?: string; level?: string }) => ['education', 'search', 'degrees', query, filters] as const,
  searchFields: (query: string, filters?: { country?: string }) => ['education', 'search', 'fields', query, filters] as const,
  searchCountries: (query: string) => ['education', 'search', 'countries', query] as const,
  searchInstitutions: (query: string, country?: string) => ['education', 'search', 'institutions', query, country] as const,
  searchLevels: (query: string, country?: string) => ['education', 'search', 'levels', query, country] as const,
};

// Stale time: 12 hours (as requested)
const STALE_TIME = 12 * 60 * 60 * 1000; // 12 hours in milliseconds

// Hook to get degrees
export const useDegrees = (filters?: { country?: string; level?: string }) => {
  return useQuery({
    queryKey: educationQueryKeys.degrees(filters),
    queryFn: () => educationDataService.getDegrees(filters),
    staleTime: STALE_TIME,
    gcTime: STALE_TIME * 2, // Keep in cache for 24 hours
    enabled: !!filters?.country, // Only fetch when country is provided
  });
};

// Hook to get fields of study
export const useFieldsOfStudy = (filters?: { country?: string; discipline?: string }) => {
  return useQuery({
    queryKey: educationQueryKeys.fieldsOfStudy(filters),
    queryFn: () => educationDataService.getFieldsOfStudy(filters),
    staleTime: STALE_TIME,
    gcTime: STALE_TIME * 2,
    enabled: !!filters?.country, // Only fetch when country is provided
  });
};

// Hook to get countries
export const useCountries = () => {
  return useQuery({
    queryKey: educationQueryKeys.countries(),
    queryFn: () => educationDataService.getCountries(),
    staleTime: STALE_TIME,
    gcTime: STALE_TIME * 2,
  });
};

// Hook to get institutions
export const useInstitutions = (country?: string) => {
  return useQuery({
    queryKey: educationQueryKeys.institutions(country),
    queryFn: () => educationDataService.getInstitutions(country),
    staleTime: STALE_TIME,
    gcTime: STALE_TIME * 2,
    // Always enabled - will fetch all institutions if no country provided
  });
};

// Hook to get education levels
export const useEducationLevels = (country?: string) => {
  return useQuery({
    queryKey: educationQueryKeys.educationLevels(country),
    queryFn: () => educationDataService.getEducationLevels(country),
    staleTime: STALE_TIME,
    gcTime: STALE_TIME * 2,
    enabled: !!country, // Only fetch when country is provided
  });
};

// Search hooks with debouncing
export const useSearchDegrees = (query: string, filters?: { country?: string; level?: string }) => {
  return useQuery({
    queryKey: educationQueryKeys.searchDegrees(query, filters),
    queryFn: () => educationDataService.searchDegrees(query, filters),
    staleTime: STALE_TIME,
    gcTime: STALE_TIME * 2,
    enabled: query.length >= 2 && !!filters?.country, // Only search when query is at least 2 characters and country is provided
  });
};

export const useSearchFieldsOfStudy = (query: string, filters?: { country?: string }) => {
  return useQuery({
    queryKey: educationQueryKeys.searchFields(query, filters),
    queryFn: () => educationDataService.searchFieldsOfStudy(query, filters),
    staleTime: STALE_TIME,
    gcTime: STALE_TIME * 2,
    enabled: query.length >= 2 && !!filters?.country, // Only search when query is at least 2 characters and country is provided
  });
};

export const useSearchCountries = (query: string) => {
  return useQuery({
    queryKey: educationQueryKeys.searchCountries(query),
    queryFn: () => educationDataService.searchCountries(query),
    staleTime: STALE_TIME,
    gcTime: STALE_TIME * 2,
    enabled: query.length >= 1, // Search countries with just 1 character
  });
};

export const useSearchInstitutions = (query: string, country?: string) => {
  return useQuery({
    queryKey: educationQueryKeys.searchInstitutions(query, country),
    queryFn: () => educationDataService.searchInstitutions(query, country),
    staleTime: STALE_TIME,
    gcTime: STALE_TIME * 2,
    enabled: query.length >= 2,
  });
};

export const useSearchEducationLevels = (query: string, country?: string) => {
  return useQuery({
    queryKey: educationQueryKeys.searchLevels(query, country),
    queryFn: () => educationDataService.searchEducationLevels(query, country),
    staleTime: STALE_TIME,
    gcTime: STALE_TIME * 2,
    enabled: query.length >= 1 && !!country, // Only search when query is provided and country is selected
  });
};